import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardHeader, CardBody, Row, Col, Table, Badge, Input } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

// Detailed Statement of Account for a merchant-loan customer: charges (fee/GST/interest/penal),
// collection buckets, outstanding, and the full payout/collection ledger. A loan selector lets the user
// view one loan at a time or the COMPLETE (all-loans) rollup. Fully defensive — every call is independent
// and falls back to "—" / empty so it never breaks the Legal tab.
const num = (n) => (n === null || n === undefined || isNaN(Number(n)) ? 0 : Number(n));
const inr = (n) =>
  n === null || n === undefined || isNaN(Number(n))
    ? "—"
    : "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const isDebit = (t) => (t || "").toLowerCase() === "debit";
const sum = (arr, f) => arr.reduce((a, x) => a + num(f(x)), 0);

const Line = ({ label, value, strong }) => (
  <div className="d-flex justify-content-between py-1" style={{ borderBottom: "1px solid #f0f0f0" }}>
    <span className={strong ? "font-weight-bold" : "text-muted"}>{label}</span>
    <span className={strong ? "font-weight-bold" : ""}>{value}</span>
  </div>
);

const PaymentStatementOfAccount = () => {
  const { cid, lid } = useParams();
  const [loans, setLoans] = useState([]);
  const [coll, setColl] = useState(null);
  const [txns, setTxns] = useState([]);
  const [selected, setSelected] = useState("all"); // "all" or a lead_id

  useEffect(() => {
    if (!cid) return;
    let active = true;
    const load = async () => {
      try {
        // All loans for this customer (no lead_id => every lead/loan row).
        const r = await axiosInstance.get(URL.LEAD_LIST, { params: { customer_id: cid } });
        const rows = (Array.isArray(r.data) ? r.data : []).filter(
          (x) => x.loan_code || x.disbursed_amount != null || x.loan_amount != null
        );
        if (active) {
          setLoans(rows);
          // Default to the loan the user navigated to (URL lead), else the complete rollup.
          const match = rows.find((x) => String(x.lead_id) === String(lid));
          setSelected(match ? String(match.lead_id) : "all");
        }
      } catch (e) {
        if (active) setLoans([]);
      }
      try {
        const r = await axiosInstance.get(URL.PAYMENT_COLLECTION_TOTAL, { params: { customer_id: cid } });
        if (active) setColl(r.data || null);
      } catch (e) {
        if (active) setColl(null);
      }
      try {
        const r = await axiosInstance.get(URL.PAYMENT_TRANSACTIONS, { params: { customer_id: cid } });
        if (active) setTxns(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (active) setTxns([]);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [cid, lid]);

  const isAll = selected === "all";

  // Aggregate the selected loan(s): money fields sum across loans; ROI/APR/tenure only apply to a single
  // loan (they are not meaningful summed), so they show "—" in the complete view.
  const view = useMemo(() => {
    const sel = isAll ? loans : loans.filter((x) => String(x.lead_id) === String(selected));
    if (sel.length === 0) return null;
    const one = sel.length === 1 ? sel[0] : null;
    return {
      count: sel.length,
      disbursed_amount: sum(sel, (x) => x.disbursed_amount),
      loan_amount: sum(sel, (x) => x.loan_amount),
      loan_processing_fee: sum(sel, (x) => x.loan_processing_fee),
      gst: sum(sel, (x) => num(x.c_gst) + num(x.s_gst) + num(x.i_gst)),
      total_interest: sum(sel, (x) => x.total_interest),
      emi_amount: sum(sel, (x) => x.emi_amount),
      total_pending_emi_amount: sum(sel, (x) => x.total_pending_emi_amount),
      total_principal: sum(sel, (x) => x.total_principal),
      roi: one ? one.roi : null,
      apr: one ? one.apr : null,
      tenure: one ? one.tenure : null,
    };
  }, [loans, selected, isAll]);

  const multi = isAll && loans.length > 1;

  // Attribute each payout (debit) to a loan by matching its disbursed amount, so the ledger can show a
  // Loan column and, when a single loan is selected, filter down to just that loan's payout(s).
  const selLoan = isAll ? null : loans.find((l) => String(l.lead_id) === String(selected));
  const loanCodeForTxn = (t) => {
    if (!isDebit(t.tranType)) return "—";
    const m = loans.find((l) => num(l.disbursed_amount) === num(t.amount));
    return m ? (m.loan_code || `#${m.lead_id}`) : "—";
  };
  const ledgerRows = selLoan
    ? txns.filter((t) => isDebit(t.tranType) && num(t.amount) === num(selLoan.disbursed_amount))
    : txns;

  return (
    <>
      {/* Loan selector */}
      <Card className="shadow-sm mb-3">
        <CardBody className="py-2 d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
          <span className="font-weight-bold" style={{ whiteSpace: "nowrap" }}>Statement for:</span>
          <Input
            type="select"
            bsSize="sm"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={{ maxWidth: "380px" }}
          >
            <option value="all">
              All Loans (Complete){loans.length ? ` — ${loans.length} loan${loans.length > 1 ? "s" : ""}` : ""}
            </option>
            {loans.map((l) => (
              <option key={l.lead_id} value={String(l.lead_id)}>
                {(l.loan_code || `Loan #${l.lead_id}`) + " — " + inr(l.disbursed_amount)}
              </option>
            ))}
          </Input>
          {multi && (
            <Badge color="info" pill>
              Complete view — totals across all {loans.length} loans
            </Badge>
          )}
        </CardBody>
      </Card>

      <Row>
        {/* Charges & Interest */}
        <Col md="6" className="mb-4">
          <Card className="shadow-sm h-100">
            <CardHeader className="py-2"><h4 className="mb-0">Charges &amp; Interest</h4></CardHeader>
            <CardBody className="py-2">
              <Line label="Disbursed Amount" value={inr(view && view.disbursed_amount)} />
              <Line label="Loan Amount (Principal)" value={inr(view && view.loan_amount)} />
              <Line label="Processing Fee" value={inr(view && view.loan_processing_fee)} />
              <Line label="GST (CGST + SGST)" value={inr(view && view.gst)} />
              <Line label="Interest" value={inr(view && view.total_interest)} />
              <Line label="Penal Charge" value={inr(0)} />
              <Line label="EMI Amount" value={inr(view && view.emi_amount)} />
              <Line label="ROI" value={view && view.roi != null ? view.roi + "%" : "—"} />
              <Line label="APR" value={view && view.apr != null ? view.apr + "%" : "—"} />
              <Line label="Tenure (days)" value={view && view.tenure != null ? view.tenure : "—"} strong />
            </CardBody>
          </Card>
        </Col>

        {/* Collection & Outstanding */}
        <Col md="6" className="mb-4">
          <Card className="shadow-sm h-100">
            <CardHeader className="py-2"><h4 className="mb-0">Collection &amp; Outstanding</h4></CardHeader>
            <CardBody className="py-2">
              <Line label="Total Collected (customer)" value={inr(coll && coll.totalCollected)} />
              <Line label="— Principal Collected" value={inr(coll && coll.principalCollection)} />
              <Line label="— Interest Collected" value={inr(coll && coll.interestCollection)} />
              <Line label="— Fee Collected" value={inr(coll && coll.feeCollection)} />
              <Line label="— Penal Collected" value={inr(coll && coll.penalSettled)} />
              <div className="my-2" />
              <Line label="Total Outstanding" value={inr(view && view.total_pending_emi_amount)} />
              <Line label="Principal Outstanding" value={inr(view && view.total_principal)} />
              <Line label="Interest Outstanding" value={inr(view && view.total_interest)} strong />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Transaction Ledger — all customer payouts/collections, or filtered to the selected loan. */}
      <Card className="shadow-sm">
        <CardHeader className="py-2 d-flex align-items-center" style={{ gap: "10px" }}>
          <h4 className="mb-0">Transaction Ledger (Payouts &amp; Collections)</h4>
          {selLoan ? (
            <Badge color="primary" pill>filtered to {selLoan.loan_code || `Loan #${selLoan.lead_id}`}</Badge>
          ) : (
            <Badge color="secondary" pill>all loans</Badge>
          )}
        </CardHeader>
        <CardBody className="py-2">
          <Table bordered hover size="sm" responsive>
            <thead className="thead-light">
              <tr>
                <th>#</th><th>Date</th><th>Type</th><th>Loan</th><th>Debit (Payout)</th>
                <th>Credit (Collection)</th><th>Account / VPA</th><th>UTR / Ref</th><th>Merchant</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ledgerRows.length > 0 ? ledgerRows.map((t, i) => {
                const debit = isDebit(t.tranType);
                return (
                  <tr key={t.id ?? i}>
                    <td>{i + 1}</td>
                    <td>{fmtDate(t.createdAt)}</td>
                    <td><Badge color={debit ? "danger" : "success"} pill>{debit ? "Payout (Debit)" : "Collection (Credit)"}</Badge></td>
                    <td>{loanCodeForTxn(t)}</td>
                    <td>{debit ? inr(t.amount) : "-"}</td>
                    <td>{debit ? "-" : inr(t.amount)}</td>
                    <td>
                      {t.accountNumber ? (
                        <div>A/c: {t.accountNumber}{t.ifscCode ? ` (${t.ifscCode})` : ""}</div>
                      ) : null}
                      {t.upiAccount ? <div>VPA: {t.upiAccount}</div> : null}
                      {!t.accountNumber && !t.upiAccount ? "-" : null}
                    </td>
                    <td>{t.utr || t.tranId || t.gatewayTransactionId || "-"}</td>
                    <td>{t.merchantCode || "-"}</td>
                    <td>{t.transactionStatus || "-"}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="10" className="text-muted">No transactions found</td></tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default PaymentStatementOfAccount;
